/**
 * VM name rules for the admin console.
 *
 * The "provision VM for user" flow reuses the customer order command
 * (`ProvisionResourceForUserCommandHandler` sends `CreateOrderForResourcesCommand`),
 * so an admin's VM name goes through the very same `VmNameRules.IsValid` on the
 * backend. Without these checks the form accepts a name the order command
 * rejects, and the admin sees an opaque 400 after filling the whole page.
 *
 * Mirrors `WiCloud.Application/Common/Helpers/VmNameRules.cs` and the customer
 * wizard's `resource.validators.ts`. A change to one needs the same change to
 * all three.
 */

export const VM_NAME_MAX_LENGTH = 64;

const RESERVED_VM_DOMAINS = ["tmtco.asia", "wiservicesplatform.com"] as const;

/**
 * Returns the reason a VM name is unacceptable, or null when it is valid.
 * The message is shown directly on the field — the admin console has no i18n.
 */
export function getVmNameIssue(name: string): string | null {
  if (!name || name.trim().length === 0) {
    return "VM name is required";
  }

  if (name.length > VM_NAME_MAX_LENGTH) {
    return `VM name must be ${VM_NAME_MAX_LENGTH} characters or fewer`;
  }

  // Underscores are excluded deliberately: invalid in a hostname (RFC 1123) and
  // refused by the backend.
  if (!/^[a-zA-Z0-9.-]+$/.test(name)) {
    return "VM name can only contain letters, numbers, hyphens (-), and dots (.)";
  }

  if (name.startsWith(".") || name.endsWith(".") || name.includes("..")) {
    return "VM name cannot start or end with a dot, or contain two dots in a row";
  }

  const labels = name.split(".");

  if (labels.length === 2) {
    return "VM name must be a single label (e.g. web-01) or a fully qualified name with at least 3 labels (e.g. web.corp.local)";
  }

  const normalizedName = name.toLowerCase();
  if (
    RESERVED_VM_DOMAINS.some(
      (domain) =>
        normalizedName === domain || normalizedName.endsWith(`.${domain}`),
    )
  ) {
    return `VM name cannot use a reserved system domain (${RESERVED_VM_DOMAINS.join(", ")})`;
  }

  const hostLabel = labels[0];

  if (hostLabel.length > VM_NAME_MAX_LENGTH) {
    return `The first label of the VM name must not exceed ${VM_NAME_MAX_LENGTH} characters`;
  }

  if (/^\d+$/.test(hostLabel)) {
    return "The first label of the VM name cannot consist of digits only";
  }

  const hasInvalidLabel = labels.some(
    (label) =>
      label.length === 0 ||
      label.length > VM_NAME_MAX_LENGTH ||
      label.startsWith("-") ||
      label.endsWith("-"),
  );

  if (hasInvalidLabel) {
    return "Each label in the VM name must not be empty and must not start or end with a hyphen (-)";
  }

  return null;
}

/** Convenience predicate over {@link getVmNameIssue}. */
export function isValidVmName(name: string): boolean {
  return getVmNameIssue(name) === null;
}
